import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import {
  Checkbox, FormControlLabel, Grid,
} from '@material-ui/core';
import {
  Panel, LoadingBounce, useDialogs, PreventTransitionPrompt,
} from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { getOptions } from '@michelin/select-options-provider';
import { cloneDeep } from 'lodash';
import { PassengerServices } from 'Types';
import { DealerAutocomplete } from 'components/Autocomplete';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import ListingFilterSelect from '@michelin/acid-components/build/components/ListingFilters/ListingFilterSelect';
import { useLocationST } from 'hooks/useLocation';
import { Controller, useForm } from 'react-hook-form';
import useService, { saveServices } from 'hooks/useService';
import CopyServices from 'components/CopyServices/CopyServices';
import { CheckboxGroup, CheckBoxItem } from 'components/CommercialServices/CheckboxGroup';
import { useSnackbar } from 'notistack';
import { FullChecboxPanel } from 'components/CommercialServices/FullCheckboxTools';
import { GenericActionButtons } from 'components/GenericActionButtons/GenericActionButtons';
import { CONTENT_AREA_HEIGHT, scrollToDisplayElementById } from '../../Utils';
import { serviceFieldLabel, serviceFieldLabels } from './ServicesFields';

interface ServicePanelProps {
  action: string,
  countryCode: string,
  data?: PassengerServices,
  mobile_only?: boolean,
  onChange?: (data: PassengerServices) => void,
}

function ServicesPanel(props: ServicePanelProps) {
  const { t } = useTranslation();
  const { stNumber, btNumber } = useParams<{ [key: string]: string }>();
  const { errorDialog } = useDialogs();
  const permissions = usePermissionsSPP();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    control, formState, getValues, setValue, reset, watch,
  } = useForm<any>({
    defaultValues: props.data,
  });

  const hideInBay = !!props.mobile_only && (props.action === 'view' || props.action === 'edit');
  const road_hazard_protection = watch('protections_and_warranties.road_hazard_protection');
  const processes_national_accounts = watch('programs.processes_national_accounts');
  const michelin_national_account = watch('programs.michelin_national_account');
  const autointegrate = watch('programs.autointegrate');
  const missingCheckboxes = !!processes_national_accounts && !michelin_national_account && !autointegrate && props.action === 'edit';
  const nafa_urban_participant = watch('programs.nafa_urban_participant');
  const roadside_service = watch('ers_services.roadside_service');
  const towing_service = watch('ers_services.towing_service');

  const isDirty = Object.keys(formState.dirtyFields).length > 0;

  const onChangeRef = useRef(props.onChange);

  useEffect(() => {
    if (onChangeRef.current) onChangeRef.current(getValues());
  }, [onChangeRef, formState, getValues]);

  useEffect(() => {
    reset(props.data);
  }, [props.data, reset]);

  const massUpdateMode = props.action === 'massUpdate';
  const btLocationQuery = useLocationST(btNumber, {
    refetchOnMount: false,
    keepPreviousData: true,
  });
  const queryClient = useQueryClient();
  const saveMutation = useMutation(saveServices, {
    onSuccess: () => queryClient.invalidateQueries(['service', stNumber]),
  });

  useEffect(() => {
    if (props.action === 'view') reset();
  }, [props.action, reset]);

  const readOnly = props.action === 'view';
  const [renders, setRenders] = useState(1);
  const forceRerender = () => setRenders(renders + 1);

  if (btLocationQuery.isLoading) return <LoadingBounce style={{ height: CONTENT_AREA_HEIGHT }} />;

  const truckAndOrUrbanPCLE195: CheckBoxItem<any>[] = [
    { key: 'vehicle_types_serviced.passenger_lt' },
    {
      key: 'vehicle_types_serviced.light_commercial',
      notes: 'up to 20-in tire sizes, excluding 17.5 and 19.5',
    },
    {
      key: 'vehicle_types_serviced.medium_commercial',
      notes: 'primarily 17.5 and 19.5-in tire sizes',
    },
    {
      key: 'vehicle_types_serviced.heavy_commercial',
      notes: '22.5-in and above tire sizes',
    },
  ];

  const processesNationalAccountItems: CheckBoxItem<any>[] = [
    {
      key: 'programs.michelin_national_account',
      disabled: !permissions.allowsAction('services.update_processes_national_accounts'),
    },
    {
      key: 'programs.autointegrate',
      disabled: !permissions.allowsAction('services.update_processes_national_accounts'),
    },
  ];

  const urbanParticipantItems: CheckBoxItem<any>[] = [
    {
      key: 'programs.mounted_wheel_service',
      disabled: !permissions.allowsAction('services.update_nafa_urban_participant'),
    },
    {
      key: 'programs.loose_tire_pickup',
      disabled: !permissions.allowsAction('services.update_nafa_urban_participant'),
    },
  ];

  const programsItems: CheckBoxItem<any>[] = [
    {
      key: 'programs.processes_national_accounts',
      disabled: !permissions.allowsAction('services.update_processes_national_accounts'),
      onRender: () => {
        if ((!massUpdateMode && !!processes_national_accounts) || massUpdateMode) {
          return (
            <CheckboxGroup
              title=""
              disableGroupSelector
              readOnly={readOnly}
              getValues={getValues}
              setValue={setValue}
              watch={watch}
              control={control}
              items={processesNationalAccountItems}
              cols={12}
              labels={serviceFieldLabels}
              style={{
                borderStyle: 'solid',
                borderColor: missingCheckboxes ? 'rgb(212, 42, 42)' : 'rgb(225, 225, 225)',
                padding: 8,
                paddingLeft: 24,
                margin: 0,
                maxWidth: '100%',
                borderWidth: 1,
                textAlign: 'left',
                whiteSpace: 'break-spaces',
              }}
            />
          );
        }
        return null;
      },
      onChange: (e, v) => {
        const data = getValues();
        if (!massUpdateMode && v && !processes_national_accounts) {
          setValue('programs.processes_national_accounts', v, { shouldDirty: true });
          setValue('programs.michelin_national_account', false, { shouldDirty: true });
          setValue('programs.autointegrate', false, { shouldDirty: true });
        }
        if (!massUpdateMode && processes_national_accounts === false) {
          data.programs.michelin_national_account = false;
          data.programs.autointegrate = false;
          setValue('programs', data.programs);
        }
      },
    },
    {
      key: 'programs.nafa_urban_participant',
      disabled: !permissions.allowsAction('services.update_nafa_urban_participant'),
      onRender: () => {
        if ((!massUpdateMode && !!nafa_urban_participant) || massUpdateMode) {
          return (
            <CheckboxGroup
              title=""
              disableGroupSelector
              readOnly={readOnly}
              getValues={getValues}
              setValue={setValue}
              watch={watch}
              control={control}
              items={urbanParticipantItems}
              cols={12}
              labels={serviceFieldLabels}
              style={{
                borderStyle: 'solid',
                borderColor: 'rgb(225, 225, 225)',
                padding: 8,
                paddingLeft: 24,
                margin: 0,
                maxWidth: '100%',
                borderWidth: 1,
                textAlign: 'left',
                whiteSpace: 'break-spaces',
              }}
            />
          );
        }
        return null;
      },
      onChange: (e, v) => {
        const data = getValues();
        if (!massUpdateMode && v && !nafa_urban_participant) {
          setValue('programs.nafa_urban_participant', v, { shouldDirty: true });
          setValue('programs.mounted_wheel_service', false, { shouldDirty: true });
          setValue('programs.loose_tire_pickup', false, { shouldDirty: true });
        }
        if (!massUpdateMode && nafa_urban_participant === false) {
          data.programs.mounted_wheel_service = false;
          data.programs.loose_tire_pickup = false;
          setValue('programs', data.programs);
        }
      },
    },
    {
      key: 'programs.rec_offroad_specialist',
      disabled: !permissions.allowsAction('services.update_rec_off_road_specialist'),
    },
    {
      key: 'programs.uhp_specialist',
      disabled: !permissions.allowsAction('services.update_ultra_high_performance_specialist'),
    },
  ];

  const ersServicesItems: CheckBoxItem<any>[] = [
    { key: 'ers_services.roadside_service', onChange: (d, v) => !v && setValue('ers_services.roadside_service_24_hrs', false, { shouldDirty: true }) },
    { key: 'ers_services.roadside_service_24_hrs', disabled: !roadside_service, onChange: (d, v) => v && setValue('ers_services.roadside_service', true, { shouldDirty: true }) },
    { key: 'ers_services.towing_service', onChange: (d, v) => !v && setValue('ers_services.towing_service_24_hrs', false, { shouldDirty: true }) },
    { key: 'ers_services.towing_service_24_hrs', disabled: !towing_service, onChange: (d, v) => v && setValue('ers_services.towing_service', true, { shouldDirty: true }) },
  ];

  const customerConvenienceItems: CheckBoxItem<any>[] = [
    { key: 'customer_convenience.waiting_area_available' },
    { key: 'customer_convenience.child_friendly_waiting_rm' },
    { key: 'customer_convenience.financing_available' },
    { key: 'customer_convenience.overnight_key_drop' },
    { key: 'customer_convenience.customer_pickup_drop_off' },
    { key: 'customer_convenience.loaner_vehicles' },
  ];

  const languagesItems: CheckBoxItem<any>[] = [
    { key: 'languages.english' },
    { key: 'languages.french' },
    { key: 'languages.spanish' },
  ];

  const save = async () => {
    if (!isDirty) return;

    try {
      const data = getValues();
      const newServices: any = cloneDeep(data);
      let newBrands: string[] | null = data.brands;
      if (newBrands && !newBrands.length) {
        newBrands = null;
        setValue('brands', null);
      }

      if (!road_hazard_protection) {
        newServices.protections_and_warranties.tire_warranty = '';
      } else if (!newServices.protections_and_warranties.tire_warranty || newServices.protections_and_warranties.tire_warranty === 'select') {
        errorDialog(
          t('Please select a tire warranty service option.'),
          t('Unable to Save Changes.'),
        );
        return;
      }

      if (!!processes_national_accounts && !michelin_national_account && !autointegrate) {
        errorDialog(
          t('Must select at least one national account processing option.'),
          t('Unable to Save Changes.'),
        );
        scrollToDisplayElementById('programs.michelin_national_account');
        return;
      }

      if (!permissions.allowsAction('services.update_rec_off_road_specialist')) {
        delete newServices.programs.rec_offroad_specialist;
      }

      if (!permissions.allowsAction('services.update_ultra_high_performance_specialist')) {
        delete newServices.programs.uhp_specialist;
      }

      const newData: any = {
        services: {
          ...newServices,
          all_brands: data.all_brands,
          brands: (!newBrands || newBrands.length === 0 ? ['All'] : newBrands),
          protections_and_warranties: {
            road_hazard_protection,
            tire_warranty: newServices.protections_and_warranties.tire_warranty,
          },
        },
      };

      await saveMutation.mutateAsync({ stNumber, data: newData });
      reset();
      forceRerender();
      setTimeout(() => {
        history.push(`/${btNumber}/retail/${stNumber}/services/view`);
        enqueueSnackbar(t('Services information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
      }, 10);
    } catch (error) {
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving Location Data.'),
      );
    }
  };

  const MyCheckbox = ({ item }: { item: CheckBoxItem<any> }) => (
    <FormControlLabel
      control={(
        <Controller
          name={item.key as any}
          control={control}
          rules={{ required: true }}
          render={(myProps) => (
            <Checkbox
              id={item.key}
              checked={myProps.field.value}
              onChange={(e) => {
                if (readOnly) return;
                myProps.field.onChange(e.target.checked);
                if (item.onChange) item.onChange(e.target.checked);
                forceRerender();
              }}
              disabled={!readOnly && item.disabled}
              readOnly={readOnly}
              color="primary"
            />
          )}
        />
      )}
      label={(
        <>
          {t(serviceFieldLabel(item.key.split('.').pop() || '', props.countryCode))}
          {item.notes && (
            <div style={{ color: '#999999', fontSize: '0.8em' }}>
              {t(item.notes)}
            </div>
          )}
        </>
      )}
    />
  );

  const renderProtectionsPanel = () => {
    const options = getOptions('short_text_road_hazard_protection_types', 'select', 'Select');
    return (
      <Panel title={t('Protections/Warranties')}>
        <Grid container spacing={2} style={{ paddingLeft: 20 }}>
          <Grid item xs={12}>
            <MyCheckbox item={{ key: 'protections_and_warranties.road_hazard_protection' }} />
          </Grid>
          {road_hazard_protection
            && (
              <Grid item xs={12}>
                <Controller
                  name="protections_and_warranties.tire_warranty"
                  control={control}
                  rules={{ required: true }}
                  render={(myProps) => (
                    <ListingFilterSelect
                      options={options}
                      key={myProps.field.value}
                      id="protections_and_warranties.tire_warranty"
                      value={myProps.field.value || 'select'}
                      onChange={(e) => { myProps.field.onChange(e.target.value); forceRerender(); }}
                      title={t('Provides tire warranty service on')}
                      readOnly={readOnly}
                    />
                  )}
                />
              </Grid>
            )}
        </Grid>
      </Panel>
    );
  };

  const renderCarBrandsPanel = () => {
    const data = getValues();
    return (
      <Panel title={t('Car Brands')}>
        <Grid container spacing={2} style={{ paddingLeft: 7 }}>
          <Grid item xs={12}>
            <MyCheckbox item={{ key: 'all_brands' }} />
          </Grid>
          {!data.all_brands
            ? (
              <Grid item xs={12}>
                <Controller
                  name="brands"
                  control={control}
                  rules={{ required: true }}
                  render={(myProps) => (
                    <DealerAutocomplete
                      id="cars"
                      variant="standard"
                      multiple
                      disableClearable
                      suggestions={getOptions('car_brands_us')}
                      placeholder={t('Select Car Brands Serviced')}
                      label={t(serviceFieldLabel('car_brands', props.countryCode))}
                      values={myProps.field.value || []}
                      readOnly={readOnly}
                      disableCloseOnSelect
                      onChange={(v: any) => { myProps.field.onChange(v); forceRerender(); }}
                    />
                  )}
                />
              </Grid>
            )
            : null}
        </Grid>
      </Panel>
    );
  };

  return (
    <>
      {!massUpdateMode && (
        <GenericActionButtons
          isDirty={isDirty}
          isSaving={saveMutation.isLoading}
          onSave={save}
          validateAction="location_information.update"
          readOnly={readOnly}
          additionalEditButtons={[
            <CopyServices
              key="copyService"
              getServicesHook={useService}
              setValue={setValue}
              type="services"
              // eslint-disable-next-line max-len
              dataPath={['languages', 'customer_convenience', 'mechanical_services', 'ers_services', 'programs', 'tire_services', 'vehicle_types_serviced', 'protections_and_warranties', 'brands', 'all_brands']}
            />,
          ]}
        />
      )}
      <Grid item md={12} lg={massUpdateMode ? 12 : 6}>
        <CheckboxGroup
          title={t('Languages')}
          disableGroupSelector
          readOnly={readOnly}
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          control={control}
          items={languagesItems}
          cols={4}
          labels={serviceFieldLabels}
        />
        <br />
        <CheckboxGroup
          title={t('Customer Convenience')}
          readOnly={readOnly}
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          control={control}
          items={customerConvenienceItems}
          cols={6}
          labels={serviceFieldLabels}
        />
        <br />
        <Panel
          title={t('Mechanical Services')}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FullChecboxPanel
                getValues={getValues}
                control={control}
                paddingLeft={0}
                labels={serviceFieldLabels}
                readOnly={readOnly}
                setValue={setValue}
                watch={watch}
                groups={[
                  {
                    label: '',
                    cols: [
                      { label: hideInBay ? '' : t('In Bay'), path: 'in_bay' },
                      { label: t('Mobile'), path: 'mobile' },
                    ],
                  },
                ]}
                items={[
                  {
                    key: 'mechanical_services.air_conditioning',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.battery',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.belts_and_hoses',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.complete_brake_service',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.front_end_axle_cv_repair',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.oil_filter_lube',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.major_mech_repair',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.service_electric_cars',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.state_inspection',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'mechanical_services.shocks_and_struts',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <CheckboxGroup
                title={t('ERS Services')}
                readOnly={readOnly}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                control={control}
                items={ersServicesItems}
                cols={6}
                labels={serviceFieldLabels}
              />
            </Grid>
          </Grid>
        </Panel>
        <br />
        {renderProtectionsPanel()}
      </Grid>
      <Grid item md={12} lg={massUpdateMode ? 12 : 6}>
        <CheckboxGroup
          title={t('Programs / National Accounts')}
          disableGroupSelector
          readOnly={readOnly}
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          control={control}
          items={programsItems}
          cols={6}
          labels={serviceFieldLabels}
        />
        <br />
        {renderCarBrandsPanel()}
        <br />
        <Panel
          title={t('Tire & Related Services')}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FullChecboxPanel
                getValues={getValues}
                control={control}
                paddingLeft={0}
                labels={serviceFieldLabels}
                readOnly={readOnly}
                setValue={setValue}
                watch={watch}
                groups={[
                  {
                    label: '',
                    cols: [
                      { label: hideInBay ? '' : t('In Bay'), path: 'in_bay' },
                      { label: t('Mobile'), path: 'mobile' },
                    ],
                  },
                ]}
                items={[
                  {
                    key: 'tire_services.all_wheel_alignment',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.computer_balancing',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.custom_wheel_sales',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.farm_tire_service',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.tire_installation_service',
                    enabledPaths: ['mobile'],
                  },
                  {
                    key: 'tire_services.rv_tire_sales',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.service_and_mount_hp_tires',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.service_and_mount_std_tires',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.service_run_flat_tires',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.service_tpms',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.tire_inflation_with_nitrogen',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.tire_repairs',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.winter_tire_studding',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                  {
                    key: 'tire_services.wheel_sales',
                    ...(hideInBay ? { enabledPaths: ['mobile'] } : { enabledPaths: ['in_bay', 'mobile'] }),
                  },
                ]}
              />
            </Grid>
          </Grid>
        </Panel>

        <br />
        <CheckboxGroup
          title={t('Vehicle Types Serviced')}
          readOnly={readOnly}
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          control={control}
          items={truckAndOrUrbanPCLE195}
          cols={6}
          labels={serviceFieldLabels}
        />
      </Grid>
      {!massUpdateMode ? (
        <PreventTransitionPrompt
          when={isDirty}
        />
      ) : null}
    </>
  );
}

export default ServicesPanel;
